import axiosV2, { apiPost } from '../core/http/axios_api_v2';
import EventHandler from './EventHandler';
import { isValidIsraeliId } from '../../../shared/israeli-id.js';

export default {




    // eventId: the row's event - the api falls back to it when the row id is gone (a rebuild, eg. a
    // status change made from the same page, gives the row a new id)
    deleteSearchResult(id, eventId){
        return apiPost(`search-results/delete/${id}`, eventId ? { eventId } : undefined);
    },


    hasTaxDocuments(customerId) {
        return axiosV2(`customers/hasTaxDocuments/${customerId}`).then(res => res.data);
    },


    deleteCustomer(customerId) {
        return apiPost(`customers/delete/${customerId}`);
    },


    // "מחיקת לקוח" on the customer card and the trash on the /customers table: the popup that asks
    // whether to mark the פניה as not relevant or really delete the customer, and does what is chosen
    // (DeleteCustomerModal, one instance in App.vue; a customer with tax documents can only be marked).
    // options: { scope: 'customer' | 'row', customerId, searchResultId, contactNames: [], event, eventsCount }
    // Resolves 'notRelevant' | 'deleted' | null (closed).
    askDeleteCustomer(options) {
        return new Promise(resolve => planup.$root.$emit('askDeleteCustomer', options, resolve));
    },


    // ── duplicate phone ─────────────────────────────────────────────────────────────────────
    // A phone that already exists in the tenant is a question, not an error: merge into that card
    // or keep a separate one. The lookup is exact-match on the server (indexed column), so it is
    // cheap enough to run on blur as well as on save.

    findCustomersByPhone(phone, excludeCustomerId) {

        if (!phone)
            return Promise.resolve([]);

        let url = `/customers/by-phone?phone=${encodeURIComponent(phone)}`;

        if (excludeCustomerId)
            url += `&excludeCustomerId=${excludeCustomerId}`;

        //a failed duplicate check must never block a save - worst case the user creates a second card
        return axiosV2(url).then(response => response.data || []).catch(() => []);
    },


    getDuplicatePair(newCustomerId, existingCustomerId) {

        return axiosV2(`/customers/duplicate-pair?newCustomerId=${newCustomerId}&existingCustomerId=${existingCustomerId}`)
            .then(response => response.data);
    },


    // options (the איחוד לקוחות כפולים tool's "מה לדרוס" choices, optional):
    //   takeFromSource: { [survivingContactId]: ['email', 'lastName', ...] } - fields taken from the
    //                   folded card even though the surviving contact already has a value
    //   businessFrom:   'source' - the folded card's סוג לקוח + business details win
    mergeCustomers(sourceCustomerId, targetCustomerId, options) {

        let body = { sourceCustomerId, targetCustomerId };

        if (options && options.takeFromSource)
            body.takeFromSource = options.takeFromSource;

        if (options && options.businessFrom)
            body.businessFrom = options.businessFrom;

        return apiPost('/customers/merge', body, true);
    },


    // הגדרות ← איחוד לקוחות כפולים: every group of cards sharing a phone / email / full name
    findDuplicateGroups(by) {

        return axiosV2(`/customers/duplicates?by=${encodeURIComponent(by || 'phone')}`).then(response => response.data);
    },


    // mirror of CustomersService.contactsAreTwins - which contacts a merge folds into one
    contactsAreTwins(a, b) {

        if (!a || !b)
            return false;

        let phoneA = this.normalizePhone(a.phone);
        let phoneB = this.normalizePhone(b.phone);

        if (phoneA && phoneB)
            return phoneA == phoneB;

        let emailA = String(a.email || '').trim().toLowerCase();
        let emailB = String(b.email || '').trim().toLowerCase();

        if (emailA && emailB && emailA == emailB)
            return true;

        let nameOf = contact => `${String(contact.firstName || '').trim()} ${String(contact.lastName || '').trim()}`.replace(/\s+/g, ' ').trim().toLowerCase();
        let nameA = nameOf(a);
        let nameB = nameOf(b);

        return !!nameA && nameA == nameB && (!phoneA || !phoneB);
    },


    // ── an untouched פנייה that a new deal should continue on ──────────────────────────────────
    // A customer whose LATEST event is still nothing but a lead (earliest status or none, no date,
    // no order lines, no documents) does not need a second event for the deal being opened: that
    // lead IS the deal, and a second event is a second row for the same customer in the table
    // (מעין מלכה: "כל פעם שאני יוצרת הזמנה חדשה זה עושה לי שני לקוחות" - customer 2175 got a blank
    // event 2189 next to its untouched lead 2175). Mirrors CustomersService.isEmptyLeadEvent.

    eventHasOrderLines(products) {

        if (!Array.isArray(products))
            return false;

        return products.some(group => group && Array.isArray(group.actualListOfProducts) && group.actualListOfProducts.length > 0);
    },


    isEmptyLeadEvent(event) {

        if (!event || !event.id)
            return false;

        if (typeof event.emptyLead == 'boolean')
            return event.emptyLead;

        if (event.planupSystemStatus && event.planupSystemStatus != 'pniaInTipul')
            return false;

        if (event.date)
            return false;

        if (event.taxDocuments && event.taxDocuments.length)
            return false;

        if (this.eventHasOrderLines(event.products))
            return false;

        return true;
    },


    // { event, index } of the customer's newest (highest id) live event when it is an empty lead,
    // null otherwise. Works on the full customer page events and on the by-phone summaries alike.
    findReusableLeadEvent(events) {

        if (!Array.isArray(events) || !events.length)
            return null;

        let newestIndex = -1;

        events.forEach((event, index) => {

            if (!event || !event.id || event.deletedAt || event.archived)
                return;

            if (newestIndex == -1 || +event.id > +events[newestIndex].id)
                newestIndex = index;
        });

        if (newestIndex == -1 || !this.isEmptyLeadEvent(events[newestIndex]))
            return null;

        return { event: events[newestIndex], index: newestIndex };
    },


    // A create form's event saved ONTO an existing empty lead: the lead's own values fill the
    // form's blanks (its name, lead source, requested dates, custom fields...) and the id makes the
    // save an update - nothing the lead already knew is lost, nothing typed is overridden.
    foldLeadIntoFormEvent(formEvent, lead) {

        let isEmpty = value => value === undefined || value === null || value === ''
            || (Array.isArray(value) && value.length == 0)
            || (typeof value == 'object' && !(value instanceof Date) && !Array.isArray(value) && Object.keys(value).length == 0);

        Object.keys(lead).forEach(key => {

            if (['id', 'customer', 'index', 'created', 'updated', 'creator'].includes(key))
                return;

            if (isEmpty(formEvent[key]) && !isEmpty(lead[key]))
                formEvent[key] = lead[key];
        });

        formEvent.id = lead.id;

        return formEvent;
    },


    //mirror of apiv2 core/helpers/phone.ts - the two sides have to agree on what "same number" is
    normalizePhone(phone) {

        let digits = String(phone == 0 || phone ? phone : '').replace(/\D/g, '');

        if (digits.indexOf('00') === 0)
            digits = digits.substring(2);

        if (digits.indexOf('972') === 0)
            digits = '0' + digits.substring(3);

        return digits;
    },


    //fields a merge may FILL on the surviving contact, never overwrite (mirrors mergeCustomers)
    foldableContactFields: ['lastName', 'email', 'teudatZehut', 'address_street', 'address_number',
        'address_city', 'address_mikud', 'image', 'title'],


    // Contacts typed into a create form, folded onto a customer that already exists: a typed
    // contact whose phone that customer already has updates THAT contact (blank fields only, so a
    // merge never overwrites what is already on the card) instead of being added a second time.
    //
    // Returns the customer's COMPLETE contact list (existing rows, some updated, plus the genuinely
    // new people) - POST /customers/contacts/:id saves the array as the customer's contacts, so
    // handing it a subset is not worth the risk.
    foldContactsIntoCustomer(typedContacts, existingContacts) {

        let handler = this;
        let contactsToSave = (existingContacts || []).map(contact => Object.assign({}, contact));

        (typedContacts || []).forEach(typed => {

            let twin = contactsToSave.find(existing => existing.phone && typed.phone &&
                handler.normalizePhone(existing.phone) == handler.normalizePhone(typed.phone));

            if (!twin) {
                //a genuinely new person on the existing card - id-less so a fresh row is inserted
                let newContact = Object.assign({}, typed);
                delete newContact.id;
                delete newContact.errors;
                contactsToSave.push(newContact);
                return;
            }

            handler.foldableContactFields.forEach(field => {
                if (!twin[field] && typed[field])
                    twin[field] = typed[field];
            });

            //the lead's own words ("מחפשים אולם ל-200 איש") are information - append, don't drop
            if (typed.comment && typed.comment != twin.comment)
                twin.comment = twin.comment ? `${twin.comment}\n${typed.comment}` : typed.comment;
        });

        contactsToSave.forEach(contact => { delete contact.errors });

        return contactsToSave;
    },


    validateCustomerContacts(component, customer) {


        let failedValidation = false;
        let foundCustomerWithPhone = false;

        // a business customer needs at least the business name (its number may come later)
        if (!this.validateBusinessDetails(component, customer))
            failedValidation = true;


        customer.contacts.map(contact => {

            if (!this.validateContact(component, contact))
                failedValidation = true;

            if (contact.phone)
                foundCustomerWithPhone = true;

        })


        if (!foundCustomerWithPhone) {
            customer.contacts[0].errors.phone = customer.contacts.length > 1 ? 'נדרש לפחות טלפון אחד ללקוח' : 'שדה חובה';
            failedValidation = true;
        }



        return !failedValidation;
    },


    // not all fields are required
    validateContact(component, contact) {




        let contactErrors = {};
        let forceOpenExtraFields = ['email', 'teudatZehut', 'address_mikud'];


        contactErrors.firstName = !contact.firstName ? 'שדה חובה' : null;
        // contactErrors.lastName = !contact.lastName ? 'שדה חובה' : null;

        contactErrors.phone = contact.phone && (!contact.phone.toString().match(/^\d{9,10}$/) || contact.phone[0] != '0') ? 'נא להזין טלפון תקין' : null;
        if (contact.phone?.match(/^\+/))
            contactErrors.phone = null;

        contactErrors.email = contact.email && !contact.email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/) ? 'נא להזין מייל תקין' : null;
        contactErrors.teudatZehut = this.teudatZehutError(contact.teudatZehut, false);
        contactErrors.address_mikud = contact.address_mikud && !contact.address_mikud.toString().match(/^\d{7}$/) ? 'נא להזין מיקוד תקין' : null;


        contactErrors.forceOpenExtraFields = !!forceOpenExtraFields.find(x => contactErrors[x]);

        component.$set(contact, 'errors', contactErrors);

        //this checks if there is at least one field with an error
        let contactIsValid = !Object.values(contactErrors).some(value => value);

        if (!contactIsValid)
            planup.$globals.notifyFormError();
        return contactIsValid;
    },


    // ת.ז.: nine digits with a valid check digit (shared/israeli-id.js). The Tax Authority refuses
    // any other number for an allocation request (432) - so a typo, or a placeholder typed to get
    // past the form (111122223), is caught here instead of on a numbered invoice. Required: an
    // empty value is an error too.
    teudatZehutError(value, required) {

        let digits = value == null ? '' : String(value).trim();

        if (!digits)
            return required ? 'נא להזין תעודת זהות תקינה' : null;
        if (!/^\d{9}$/.test(digits))
            return 'נא להזין תעודת זהות תקינה (9 ספרות)';
        if (!isValidIsraeliId(digits))
            return 'מספר תעודת הזהות אינו תקין (ספרת ביקורת שגויה)';
        return null;
    },


    // requireTeudatZehut: below the regulatory amount (and unless the business opted into always
    // enforcing) the id is optional - but when a value was typed it must still be a valid one
    validateContactForTaxDocument(component, contact, requireTeudatZehut = true) {

        let contactErrors = {};

        contactErrors.firstName = !contact.firstName ? 'שדה חובה' : null;
        // contactErrors.lastName = !contact.lastName ? 'שדה חובה' : null;
        contactErrors.phone = contact.phone && (!contact.phone.toString().match(/^\d{9,10}$/) || contact.phone[0] != '0') ? 'נא להזין טלפון תקין' : null;
        if (contact.phone?.match(/^\+/))
            contactErrors.phone = null;

        contactErrors.email = contact.email && !contact.email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/) ? 'נא להזין מייל תקין' : null;
        contactErrors.teudatZehut = this.teudatZehutError(contact.teudatZehut, requireTeudatZehut);

        contactErrors.address_mikud = contact.address_mikud && !contact.address_mikud.toString().match(/^\d{7}$/) ? 'נא להזין מיקוד תקין' : null;
        // contactErrors.address_street = !contact.address_street ? 'שדה חובה' : null;
        // contactErrors.address_number = !contact.address_number ? 'שדה חובה' : null;
        // contactErrors.address_city = !contact.address_city ? 'שדה חובה' : null;


        component.$set(contact, 'errors', contactErrors);

        //this checks if there is at least one field with an error
        let contactIsValid = !Object.values(contactErrors).some(value => value);

        if (!contactIsValid)
            planup.$globals.notifyFormError();

        return contactIsValid;



    },


    // ---- סוג לקוח (פרטי / עסקי) ----
    // A business customer is invoiced on the business fields (name / ח.פ. / address) instead of on
    // the contact; the contacts stay the people to talk to. Mirrors apiv2 customers/business-customer.ts.

    businessIdTypeOptions: [
        { value: 'company', name: 'חברה בע"מ', label: 'ח.פ.' },
        { value: 'osekMurshe', name: 'עוסק מורשה', label: 'ע.מ.' },
        { value: 'osekPatur', name: 'עוסק פטור', label: 'מספר עוסק' },
        { value: 'amuta', name: 'עמותה', label: 'ע.ר.' },
        { value: 'other', name: 'אחר', label: 'מספר מזהה' },
    ],

    businessFields: ['customerType', 'businessName', 'businessIdType', 'businessNumber',
        'businessAddressStreet', 'businessAddressNumber', 'businessAddressCity', 'businessAddressMikud'],

    isBusinessCustomer(customer) {
        return !!customer && customer.customerType == 'business';
    },

    businessIdLabel(idType) {
        let option = this.businessIdTypeOptions.find(option => option.value == idType);
        return option ? option.label : 'ח.פ.';
    },

    // 'ח.פ. 512345678' - empty when the customer has no number
    businessNumberText(customer) {
        if (!customer || !customer.businessNumber)
            return '';
        return `${this.businessIdLabel(customer.businessIdType)} ${customer.businessNumber}`;
    },

    generateLocalEmptyBusinessDetails() {
        return { customerType: 'private', businessName: null, businessIdType: 'company', businessNumber: null,
            businessAddressStreet: null, businessAddressNumber: null, businessAddressCity: null, businessAddressMikud: null };
    },

    // requireNumber: on a tax document the ח.פ. / מספר עוסק follows the same amount rule as a private
    // customer's ת.ז. (see CreateTaxDocument.isTeudatZehutRequired); on the customer forms it is
    // optional - but a typed one must be a valid one
    validateBusinessDetails(component, customer, requireNumber = false) {

        let errors = {};

        if (this.isBusinessCustomer(customer)) {

            errors.businessName = !customer.businessName || !String(customer.businessName).trim() ? 'שדה חובה' : null;

            let number = customer.businessNumber ? String(customer.businessNumber).trim() : '';
            let numberIsValid = customer.businessIdType == 'other' ? /^\d{5,15}$/.test(number) : /^\d{9}$/.test(number);

            if (requireNumber && !number)
                errors.businessNumber = 'שדה חובה';
            else if (number && !numberIsValid)
                errors.businessNumber = customer.businessIdType == 'other' ? 'נא להזין מספר תקין' : `נא להזין ${this.businessIdLabel(customer.businessIdType)} תקין (9 ספרות)`;
            // ח.פ / מספר עוסק / ע.ר carry the same check digit as a ת.ז - the Tax Authority refuses
            // a number without it (432)
            else if (number && customer.businessIdType != 'other' && !isValidIsraeliId(number))
                errors.businessNumber = `${this.businessIdLabel(customer.businessIdType)} אינו תקין (ספרת ביקורת שגויה)`;
            else
                errors.businessNumber = null;

            errors.businessAddressMikud = customer.businessAddressMikud && !String(customer.businessAddressMikud).match(/^\d{7}$/) ? 'נא להזין מיקוד תקין' : null;
        }

        component.$set(customer, 'businessErrors', errors);

        let valid = !Object.values(errors).some(value => value);

        if (!valid)
            planup.$globals.notifyFormError();

        return valid;
    },

    // POST customers/business-details/:id - סוג לקוח + the business fields of an existing customer
    // (a new customer posts them inside the customer object). Skipped for an object that never
    // carried the type (nothing to say), so old callers keep working untouched.
    async saveBusinessDetails(customer, hideSuccessAlert = true) {

        if (!customer || !customer.id || customer.customerType === undefined)
            return null;

        let data = {};

        this.businessFields.forEach(field => {
            if (customer[field] !== undefined)
                data[field] = customer[field];
        });

        return apiPost(`/customers/business-details/${customer.id}`, data, hideSuccessAlert);
    },


    isCustomerRelevant(customer) {
        return !(customer?.planupCustomerStatus?.type?.value === 0);
    },






    planupCustomerStatusTypeOptions: [
        { name: 'לא רלוונטי', value: 0 },
        { name: 'אופציה לאירוע', value: 1 },
        { name: 'אירוע סגור', value: 2 }
    ],


    //for message, contact or crewmember
    getDisplayName(messageOrcontact) {

        switch (true) {
            case !!messageOrcontact.name:
                return messageOrcontact.name;
            case !!(messageOrcontact.firstName && messageOrcontact.lastName):
                return `${messageOrcontact.firstName} ${messageOrcontact.lastName}`
            case !!messageOrcontact.firstName:
                return messageOrcontact.firstName;
            case !!messageOrcontact.lastName:
                return messageOrcontact.lastName;
            case !!messageOrcontact.emailAddress:
                return messageOrcontact.emailAddress;
            case !!messageOrcontact.email:
                return messageOrcontact.email;
        }

        return '';
    },





    getContactData(contactId) {

        return axiosV2(`customer-contact/${contactId}`).then(res => res.data);
    },

    //the id of the customer card a contact sits on - null when it has none (an orphaned row)
    getContactCustomerId(contactId) {

        return axiosV2(`customer-contact/getCustomer/${contactId}`).then(res => res.data || null);
    },



    //Fallback for an avatar <img> that failed to load. Contacts whose `pic` points at a missing or
    //corrupt S3 object exist in the wild (getProfilePic used to upload whatever baileys answered —
    //error strings included — so those rows hold 10-byte "jpegs"), and without this the browser
    //paints its broken-image glyph. Fall back to the name's letter avatar, then to the generic
    //silhouette; the third stage leaves the src alone so a dead URL can't loop the handler.
    onContactImageError(event, messageOrcontact) {

        let img = event && event.target;

        if (!img)
            return;

        let stage = img.getAttribute('data-picfallback');

        if (!stage) {
            img.setAttribute('data-picfallback', 'letter');
            img.src = planup.$globals.getLetterImage(this.getDisplayName(messageOrcontact));
            return;
        }

        if (stage == 'letter') {
            img.setAttribute('data-picfallback', 'generic');
            img.src = 'https://bucket2.mit4mit.co.il/yoni/user.png';
        }

    },


    //for message, contact or crewmember
    getContactImage(messageOrcontact) {

        if (!messageOrcontact){
            return 'https://bucket2.mit4mit.co.il/yoni/user.png';
        }

        if (messageOrcontact.pic) {
            return `https://planup-il.s3.eu-central-1.amazonaws.com/${messageOrcontact.pic}`;
        }

        let a = this.getDisplayName(messageOrcontact);

        return planup.$globals.getLetterImage(this.getDisplayName(messageOrcontact))

    },



    // helper functions
    //////////////////////

    generateLocalEmptyCustomer() {
        return { contacts: [this.generateLocalEmptyContact()], leadId: null, notRelevant: false, leadSource: null, planupCustomerStatus: null, ...this.generateLocalEmptyBusinessDetails() }
    },

    generateLocalEmptyContact() {
        return { firstName: null, lastName: null, phone: null, email: null, teudatZehut: null, address_mikud: null, address_street: null, address_number: null, address_city: null, showInTaxDocument:true }
    },

    doesCustomerExistOnServer(customer) {
        return !!customer.id;
    },


    buildContactsSummaryForTimeline(customer) {


        let timelineSummary = '';

        for (let i = 0; i < customer.contacts.length; i++) {
            timelineSummary += customer.contacts[i].firstName + ', ';
        }

        timelineSummary = timelineSummary.slice(0, -2);


        return timelineSummary;


    },


    // leadToCustomer(leadId) {


    //     return apiPost(`/customers/leadToCustomer/${leadId}`)

    // },


    getLead(leadId) {

        return axiosV2(`/customers/getLead/${leadId}`).then(response => response.data);

    },


    // getting functions
    //////////////////////

    async getCustomer(id) {

        return axiosV2("/customers/" + id).then(

            response => {

                let customer = response.data;

                for (let i = 0; i < customer.events?.length; i++) {
                    EventHandler.prepEventFromServerToVue(customer.events[i]);
                    customer.events[i].index = i;  //adding index to events
                }

                return customer;
            }
        );
    },

    async getMinimalData(id) {

        return axiosV2("/customers/minimalData/" + id).then(

            response => response.data
        )
    },




    // saving functions
    //////////////////////


    async saveCustomerContacts(customerData, hideSuccessAlert, callback) {

        if (this.doesCustomerExistOnServer(customerData)) {

            return await this.updateContacts(customerData, callback, hideSuccessAlert);

        } else {

            return await this.createNewCustomer(customerData, callback, hideSuccessAlert);

        }
    },



    async createNewCustomer(customerData, callback, hideSuccessAlert) {

        return apiPost('/customers', customerData, hideSuccessAlert).then(
            savedCustomer => {
                customerData.id = savedCustomer.id;
                customerData.contacts = savedCustomer.contacts;
                if (callback)
                    callback();
            });
    },


    async updateContacts(customerData, callback, hideSuccessAlert) {

        // סוג לקוח + the business fields live on the customer row, not on the contacts
        await this.saveBusinessDetails(customerData, true);

        return apiPost(`/customers/contacts/${customerData.id}`, customerData.contacts, hideSuccessAlert).then(x => callback ? callback(x) : null)
    },



    // navigation functions


    // goToAddCustomerOnlyContacts(){
    //     planup.$router.push(`/customer/?onlyContacts`)
    // },

    goToCustomer(customer) {
        planup.$router.push(`/customer/${customer.id}`)
    },


    goToSearchResult(searchResult) {


        if (searchResult.eventId) {

            planup.$router.push(`/customer/${searchResult.customerId}?eventId=${searchResult.eventId}`)

        } else {

            planup.$router.push(`/customer/${searchResult.customerId}`)

        }


    },



    sendMessage(customerId, messageData) {
        return apiPost(`customers/sendMessage/${customerId}`, messageData);
    },

    goToAddCustomerOnlyMeeting(date,time) {

        planup.$root.$emit('hideQuickCalendar');

        if (date) {

            let url = `/customer?onlyMeeting&dateForMeeting=${date.getTime()}`;
            if (url)
                url += `&timeForMeeting=${time}`;
            
            planup.$router.push(url)
            return;
        }

        planup.$router.push(`/customer?onlyMeeting`)
    },


    goToAddCustomerOnlyTask(date) {

        planup.$root.$emit('hideQuickCalendar');

        if (date) {

            planup.$router.push(`/customer?onlyTask&dateForTask=${date.getTime()}`)
            return;
        }

        planup.$router.push(`/customer?onlyTask`)
    },




    goToAddCustomerAddEvent(date,time) {


        planup.$root.$emit('hideQuickCalendar');

        if (date) {

            let url = `/customer?dateForEvent=${date.getTime()}`
            if (time) {
                url += `&timeForEvent=${time}`
            }
            planup.$router.push(url);


            return;
        }

        planup.$router.push(`/customer`)

    },



    getCustomerEmail(customer) {

        if (!customer?.contacts?.length) {
            return '';
        }

        for (let i = 0; i < customer.contacts.length; i++) {
            if (customer?.contacts[i]?.email?.length)
                return customer.contacts[i].email;
        }

    },



    getCustomerSearchResult(customerId) {

        return (axiosV2(`/search-results/getCustomerSearchResult/${customerId}`)).then(response => response.data);
    }












}